import { useState } from "react";
import { MainLayout } from "@/components/layout/MainLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { KeyRotationDialog } from "@/components/admin/security/KeyRotationDialog";
import { KeyHistoryTable } from "@/components/admin/security/KeyHistoryTable";
import { EncryptionPoliciesForm } from "@/components/admin/security/EncryptionPoliciesForm";
import { MasterKeyManagement } from "@/components/admin/security/MasterKeyManagement";
import { EmergencyKeyRecovery } from "@/components/admin/security/EmergencyKeyRecovery";
import { ComplianceSettings } from "@/components/admin/security/ComplianceSettings";
import {
  useCurrentKeyInfo,
  useKeyHistory,
  useRotateKey,
  useVerifyKey,
  hashKey,
} from "@/hooks/useEncryptionKeyManagement";
import { useComplianceStatus, useEncryptionPolicy } from "@/hooks/useEncryptionPolicies";
import { toast } from "sonner";
import {
  Key,
  RefreshCw,
  Shield,
  ShieldCheck,
  ShieldX,
  ShieldAlert,
  Clock,
  Loader2,
  AlertTriangle,
  Settings2,
  KeyRound,
  Lock,
  FileText,
} from "lucide-react";
import { format } from "date-fns";

export default function EncryptionKeyManagement() {
  const [rotateDialogOpen, setRotateDialogOpen] = useState(false);
  const [verifyKey, setVerifyKey] = useState("");
  const [verifyResult, setVerifyResult] = useState<boolean | null>(null);
  const [activeTab, setActiveTab] = useState("overview");

  // Queries and mutations for global key (tenant_id = null)
  const { data: currentKey, isLoading: loadingCurrent, error: currentKeyError, refetch: refetchCurrent } = useCurrentKeyInfo(null);
  const { data: history, isLoading: loadingHistory, error: historyError, refetch: refetchHistory } = useKeyHistory(null);
  const { data: compliance, isLoading: loadingCompliance } = useComplianceStatus(null);
  const { data: policy, isLoading: loadingPolicy } = useEncryptionPolicy(null);
  const rotateKeyMutation = useRotateKey();
  const verifyKeyMutation = useVerifyKey();

  // Calculate rotation lifecycle status
  const rotationPeriodDays = policy?.rotation_period_days ?? 90;
  const rotationWarningDays = policy?.rotation_warning_days ?? 14;
  const daysSinceRotation = currentKey?.days_since_rotation ?? 0;
  const daysUntilRotation = Math.max(0, rotationPeriodDays - daysSinceRotation);
  const isRotationOverdue = daysSinceRotation > rotationPeriodDays;
  const isRotationWarning = !isRotationOverdue && daysUntilRotation <= rotationWarningDays;
  const rotationProgress = Math.min(100, (daysSinceRotation / rotationPeriodDays) * 100);

  // Check for permission errors
  const isPermissionError = (error: unknown): boolean => {
    if (!error) return false;
    const message = error instanceof Error ? error.message : String(error);
    return message.toLowerCase().includes("access denied") ||
           message.toLowerCase().includes("platform admin") ||
           message.toLowerCase().includes("403");
  };

  const hasPermissionError = isPermissionError(currentKeyError) || isPermissionError(historyError);

  const handleRotateKey = async (newKey: string, reason: string) => {
    try {
      const keyHash = await hashKey(newKey);
      const newVersion = await rotateKeyMutation.mutateAsync({
        tenant_id: null,
        key_hash: keyHash,
        reason,
      });
      toast.success(`Key rotated successfully. New version: ${newVersion}`);
      setRotateDialogOpen(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to rotate key");
    }
  };

  const handleVerifyKey = async () => {
    if (!verifyKey) {
      toast.error("Please enter a key to verify");
      return;
    }

    try {
      const keyHash = await hashKey(verifyKey);
      const isValid = await verifyKeyMutation.mutateAsync({
        tenant_id: null,
        key_hash: keyHash,
      });
      setVerifyResult(isValid);
      if (isValid) {
        toast.success("Key verification successful - key matches");
      } else {
        toast.error("Key verification failed - key does not match");
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to verify key");
      setVerifyResult(false);
    }
  };

  const handleRefresh = () => {
    refetchCurrent();
    refetchHistory();
    toast.success("Data refreshed");
  };

  return (
    <MainLayout>
      <div className="p-4 sm:p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Encryption Key Management</h1>
            <p className="text-muted-foreground">
              Manage encryption keys, policies, and compliance settings
            </p>
          </div>
          <Button variant="outline" onClick={handleRefresh}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Permission Error Alert */}
        {hasPermissionError && (
          <Alert variant="destructive">
            <ShieldX className="h-4 w-4" />
            <AlertTitle>Access Denied</AlertTitle>
            <AlertDescription>
              You do not have permission to access encryption key management.
              This feature requires platform administrator privileges.
            </AlertDescription>
          </Alert>
        )}

        {/* Compliance Status Banner */}
        {!loadingCompliance && compliance && compliance.compliance_mode !== "none" && (
          <Alert variant={compliance.is_compliant ? "default" : "destructive"}>
            {compliance.is_compliant ? (
              <ShieldCheck className="h-4 w-4" />
            ) : (
              <ShieldAlert className="h-4 w-4" />
            )}
            <AlertTitle>
              {compliance.is_compliant ? "Compliant" : "Compliance Issues Detected"}
            </AlertTitle>
            <AlertDescription>
              {compliance.is_compliant
                ? `System is compliant with ${compliance.compliance_mode.toUpperCase()} requirements.`
                : `Review the Compliance tab to resolve ${compliance.issues?.length || 0} issue(s).`}
            </AlertDescription>
          </Alert>
        )}

        {/* Current Key Status */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Key className="h-5 w-5" />
                  Current Key Status
                </CardTitle>
                <CardDescription>Information about the currently active encryption key</CardDescription>
              </div>
              {currentKey && (
                <Button
                  variant={isRotationOverdue ? "destructive" : isRotationWarning ? "secondary" : "outline"}
                  onClick={() => setRotateDialogOpen(true)}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Rotate Key
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loadingCurrent || loadingPolicy ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading key information...
              </div>
            ) : currentKey ? (
              <div className="space-y-6">
                {/* Key Info Grid */}
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Version</p>
                    <p className="font-medium">{currentKey.key_version}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Key Hash</p>
                    <code className="bg-muted px-2 py-1 rounded text-sm">
                      {currentKey.key_hash_masked}
                    </code>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Last Rotated</p>
                    <p className="font-medium flex items-center gap-1">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      {format(new Date(currentKey.rotated_at), "MMM d, yyyy")}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Days Since Rotation</p>
                    <Badge
                      variant={isRotationOverdue ? "destructive" : isRotationWarning ? "secondary" : "outline"}
                    >
                      {currentKey.days_since_rotation} days
                    </Badge>
                  </div>
                </div>

                {/* Key Lifecycle Indicator */}
                <div className="rounded-lg border p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Key Rotation Lifecycle</span>
                    </div>
                    {isRotationOverdue ? (
                      <Badge variant="destructive">
                        Overdue by {daysSinceRotation - rotationPeriodDays} days
                      </Badge>
                    ) : isRotationWarning ? (
                      <Badge variant="secondary">
                        Due in {daysUntilRotation} days
                      </Badge>
                    ) : (
                      <Badge variant="outline">
                        {daysUntilRotation} days remaining
                      </Badge>
                    )}
                  </div>

                  <div className="space-y-1">
                    <Progress
                      value={rotationProgress}
                      className={`h-2 ${
                        isRotationOverdue
                          ? "[&>div]:bg-destructive"
                          : isRotationWarning
                          ? "[&>div]:bg-yellow-500"
                          : ""
                      }`}
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Created</span>
                      <span>Rotation required after {rotationPeriodDays} days</span>
                    </div>
                  </div>

                  {policy?.enforce_rotation && (
                    <div className="flex items-center gap-2 text-sm">
                      <Shield className="h-4 w-4" />
                      <span>
                        Enforcement mode:{" "}
                        <span className="font-medium capitalize">
                          {policy.enforcement_mode}
                        </span>
                        {policy.enforcement_mode === "block" && (
                          <span className="text-muted-foreground">
                            {" "}(encryption will be blocked when overdue)
                          </span>
                        )}
                        {policy.enforcement_mode === "strict" && (
                          <span className="text-destructive">
                            {" "}(encryption blocked + critical alert)
                          </span>
                        )}
                        {policy.enforcement_mode === "warn" && (
                          <span className="text-muted-foreground">
                            {" "}(warnings logged, encryption allowed)
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>No Key Found</AlertTitle>
                <AlertDescription>
                  No encryption key has been configured yet. Click "Rotate Key" to set up the
                  initial encryption key.
                </AlertDescription>
              </Alert>
            )}

            {/* Critical Warning for Overdue Keys */}
            {currentKey && isRotationOverdue && (
              <Alert variant="destructive" className="mt-4">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Key Rotation Overdue - Immediate Action Required</AlertTitle>
                <AlertDescription className="space-y-2">
                  <p>
                    The encryption key is {currentKey.days_since_rotation} days old, which exceeds
                    the configured rotation period of {rotationPeriodDays} days.
                  </p>
                  {policy?.enforce_rotation && policy.enforcement_mode !== "warn" && (
                    <p className="font-medium">
                      {policy.enforcement_mode === "strict"
                        ? "All encryption operations are currently BLOCKED until the key is rotated."
                        : "New encryption operations are currently BLOCKED until the key is rotated."}
                    </p>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => setRotateDialogOpen(true)}
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Rotate Key Now
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            {/* Warning for Keys Approaching Rotation */}
            {currentKey && isRotationWarning && (
              <Alert className="mt-4">
                <Clock className="h-4 w-4" />
                <AlertTitle>Key Rotation Due Soon</AlertTitle>
                <AlertDescription>
                  The encryption key will require rotation in {daysUntilRotation} days.
                  Plan your key rotation to avoid service disruption.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-2 lg:grid-cols-6">
            <TabsTrigger value="overview" className="flex items-center gap-1">
              <Key className="h-4 w-4 hidden sm:inline" />
              <span>Overview</span>
            </TabsTrigger>
            <TabsTrigger value="policies" className="flex items-center gap-1">
              <Settings2 className="h-4 w-4 hidden sm:inline" />
              <span>Policies</span>
            </TabsTrigger>
            <TabsTrigger value="master-keys" className="flex items-center gap-1">
              <KeyRound className="h-4 w-4 hidden sm:inline" />
              <span>Master Keys</span>
            </TabsTrigger>
            <TabsTrigger value="recovery" className="flex items-center gap-1">
              <Lock className="h-4 w-4 hidden sm:inline" />
              <span>Recovery</span>
            </TabsTrigger>
            <TabsTrigger value="compliance" className="flex items-center gap-1">
              <Shield className="h-4 w-4 hidden sm:inline" />
              <span>Compliance</span>
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-1">
              <FileText className="h-4 w-4 hidden sm:inline" />
              <span>History</span>
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              {/* Rotate Key Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <RefreshCw className="h-5 w-5" />
                    Rotate Encryption Key
                  </CardTitle>
                  <CardDescription>
                    Generate a new encryption key and deprecate the current one
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Alert>
                    <Shield className="h-4 w-4" />
                    <AlertDescription>
                      Key rotation is a critical operation. Ensure you have backed up your current
                      key before proceeding.
                    </AlertDescription>
                  </Alert>
                  <Button variant="destructive" onClick={() => setRotateDialogOpen(true)}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Rotate Encryption Key
                  </Button>
                </CardContent>
              </Card>

              {/* Verify Key Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ShieldCheck className="h-5 w-5" />
                    Verify Encryption Key
                  </CardTitle>
                  <CardDescription>
                    Verify that a key matches the currently active encryption key
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="verify-key">Enter Key to Verify</Label>
                    <div className="flex gap-2">
                      <Input
                        id="verify-key"
                        type="password"
                        placeholder="Enter the encryption key to verify"
                        value={verifyKey}
                        onChange={(e) => {
                          setVerifyKey(e.target.value);
                          setVerifyResult(null);
                        }}
                        className="flex-1"
                      />
                      <Button
                        onClick={handleVerifyKey}
                        disabled={verifyKeyMutation.isPending || !verifyKey}
                      >
                        {verifyKeyMutation.isPending && (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        )}
                        Verify
                      </Button>
                    </div>
                  </div>

                  {verifyResult !== null && (
                    <Alert variant={verifyResult ? "default" : "destructive"}>
                      {verifyResult ? (
                        <>
                          <ShieldCheck className="h-4 w-4" />
                          <AlertTitle>Key Verified</AlertTitle>
                          <AlertDescription>
                            The provided key matches the currently active encryption key.
                          </AlertDescription>
                        </>
                      ) : (
                        <>
                          <ShieldX className="h-4 w-4" />
                          <AlertTitle>Key Mismatch</AlertTitle>
                          <AlertDescription>
                            The provided key does not match the currently active encryption key.
                          </AlertDescription>
                        </>
                      )}
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Quick Stats */}
            <div className="grid gap-4 md:grid-cols-5">
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>Current Version</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold">
                    {loadingCurrent ? "-" : currentKey?.key_version || "None"}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>Days Until Rotation</CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingCurrent || loadingPolicy ? (
                    <p className="text-2xl font-bold">-</p>
                  ) : isRotationOverdue ? (
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-destructive" />
                      <span className="text-2xl font-bold text-destructive">Overdue</span>
                    </div>
                  ) : (
                    <p className={`text-2xl font-bold ${isRotationWarning ? "text-yellow-600" : ""}`}>
                      {daysUntilRotation}
                    </p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>Enforcement Mode</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold capitalize">
                    {loadingPolicy ? "-" : policy?.enforce_rotation ? policy.enforcement_mode : "Disabled"}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>Compliance Mode</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold capitalize">
                    {loadingCompliance ? "-" : compliance?.compliance_mode || "None"}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>Compliance Status</CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingCompliance ? (
                    <p className="text-2xl font-bold">-</p>
                  ) : (
                    <Badge
                      variant={compliance?.is_compliant ? "default" : "destructive"}
                      className="text-lg px-3 py-1"
                    >
                      {compliance?.is_compliant ? "Compliant" : "Non-Compliant"}
                    </Badge>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Policies Tab */}
          <TabsContent value="policies">
            <EncryptionPoliciesForm tenantId={null} />
          </TabsContent>

          {/* Master Keys Tab */}
          <TabsContent value="master-keys">
            <MasterKeyManagement />
          </TabsContent>

          {/* Recovery Tab */}
          <TabsContent value="recovery">
            <EmergencyKeyRecovery />
          </TabsContent>

          {/* Compliance Tab */}
          <TabsContent value="compliance">
            <ComplianceSettings tenantId={null} />
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Key Rotation History
                </CardTitle>
                <CardDescription>
                  View the history of all encryption key rotations
                </CardDescription>
              </CardHeader>
              <CardContent>
                <KeyHistoryTable history={history || []} isLoading={loadingHistory} />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Rotation Dialog */}
        <KeyRotationDialog
          open={rotateDialogOpen}
          onOpenChange={setRotateDialogOpen}
          onConfirm={handleRotateKey}
          isLoading={rotateKeyMutation.isPending}
        />
      </div>
    </MainLayout>
  );
}
